import ViewOff16 from '@carbon/icons-react/es/view--off/16';
import View16 from '@carbon/icons-react/es/view/16';
import { Tooltip } from 'antd';
import classNames from 'classnames/bind';
import { uniqBy } from 'lodash';
import React, { useEffect, useState, FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { ProjectId } from 'api/models';
import { PartnerPermissionName } from 'api/partner/types';
import { Avatar } from 'components/atoms/avatar';
import { Button } from 'components/atoms/button';
import { RichTextEditor } from 'components/atoms/rich-text-editor';
import { RTE } from 'components/atoms/rte';
import { ContainedList } from 'components/organisms/notifications/list/line-item/contained-list';
import { useProjectDetail } from 'data/projects/hooks';
import { useAccount } from 'hooks/use-account';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { fullName } from 'utils/full-name';
import { queueMacrotask } from 'utils/macrotask';
import styles from './custom-form.module.css';

const CompanyNames = styled.span`
  > span {
    margin-left: 0;
  }
`;

interface Props {
  isPrivate: boolean;
  initialFormVisibility?: boolean;
  hidePrivateToggler?: boolean;
  readonly?: boolean;
  placeholder?: string;
  focusTrigger?: string;
  initialData?: string;
  submitFormLabel?: string;
  onPrivateBtnClick?: () => void;
  onSubmit: (content: string) => void;
  onClose?: () => void;
  isAvatarInvisible?: boolean;
  projectId: number; // Note: I have to add projectId due to a compulsion of RTE mentions.
  onChange?: (value: string) => void;
  conversationPrivacy: {
    private: boolean;
    shared: boolean;
    toggle: boolean;
  };
}

const ReplyComment = ({ onClick, avatarSrc, userName }) => {
  const reply = useTranslation('reply_small');

  return (
    // eslint-disable-next-line
    <div className={styles.reply_helper} onClick={onClick}>
      <div className={styles.avatar__wrapper}>
        <span className={styles.avatar}>
          <Avatar size="small" src={avatarSrc} name={userName} />
        </span>
      </div>
      <span>{reply}</span>
    </div>
  );
};

export const FormWithPrivacyHandler: FC<Props> = ({
  isPrivate = false,
  onPrivateBtnClick,
  onSubmit,
  onClose = () => null,
  focusTrigger,
  initialFormVisibility = false,
  readonly = false,
  initialData = '',
  submitFormLabel,
  placeholder = '',
  hidePrivateToggler,
  projectId,
  isAvatarInvisible,
  onChange,
  conversationPrivacy,
}: Props) => {
  const [rte, setRte] = useState<RichTextEditor | null>(null);
  const [commentContent, setContent] = useState(initialData);
  const [waitingForAPI, setAPIStatus] = useState(false);
  const [showForm, setFormVisibility] = useState(initialFormVisibility);

  const userInfo = useUser();
  const user = userInfo && userInfo.user;
  const userName = user ? fullName(user) : '';
  const avatarSrc = user?.photoUrl;
  const { formatMessage: f } = useIntl();

  const buttonClass = classNames(styles.toggle_btn, {
    [styles.toggle_btn__private]: isPrivate || conversationPrivacy.private,
    [styles.toggle_btn__disabled]: hidePrivateToggler,
  });

  const editorClass = classNames(styles.rte__wrapper, {
    [styles.rte__wrapper_private]: isPrivate || conversationPrivacy.private,
  });

  useEffect(() => {
    queueMacrotask(() => {
      rte?.focus();
    });
  }, [rte, focusTrigger]);

  const onFormSubmit = () => {
    if (commentContent.length > 1) {
      setAPIStatus(true);
      onSubmit(commentContent);
      window.setTimeout(() => {
        setFormVisibility(false);
        setAPIStatus(false);
        setContent('');
      }, 900);
    }
  };
  const { data: projectData } = useProjectDetail(ProjectId(projectId));
  const { company: vendorCompany } = useAccount();

  const permissivePartnersInProject = useMemo(
    () =>
      (projectData?.partners ?? []).filter(
        ({ accessLevel }) =>
          accessLevel?.partnerPermissionSetName ===
          PartnerPermissionName.Permissive,
      ),
    [projectData?.partners],
  );
  const sectionVisibleTo = useMemo(
    () =>
      uniqBy(
        [user.company, vendorCompany].concat(permissivePartnersInProject),
        'companyId',
      ).map(({ companyName }) => companyName),
    [user.company, permissivePartnersInProject, vendorCompany],
  );

  return showForm ? (
    <form
      className={`${styles.form} ${styles['form--ready']}`}
      onSubmit={onFormSubmit}
    >
      <div className={styles.editor__wrapper}>
        {isAvatarInvisible ? (
          <div className={styles.avatar__wrapper}>
            <span className={styles.avatar}>
              <Avatar size="small" src={avatarSrc} name={userName} />
            </span>
          </div>
        ) : null}
        <div className={`${editorClass} form__editor`}>
          <RTE
            projectId={ProjectId(projectId)}
            className={styles.rte}
            disallowImages
            ref={(el) => setRte(el)}
            onChange={(data) => {
              setContent(data);
              onChange?.(data);
            }}
            data={initialData}
            onEnter={onFormSubmit}
            placeholder={placeholder}
            readonly={readonly}
          />

          <Tooltip
            title={
              conversationPrivacy.private || conversationPrivacy.shared
                ? ''
                : f(
                    {
                      id: 'conversations.tooltips.send_a_message',
                    },
                    {
                      companyName: (
                        <CompanyNames>
                          <ContainedList
                            show={sectionVisibleTo.length}
                            items={sectionVisibleTo}
                          />
                        </CompanyNames>
                      ),
                      private: isPrivate,
                    },
                  )
            }
            placement="left"
          >
            <span className={styles.toggle_btn_wrapper}>
              <button
                type="button"
                className={buttonClass}
                onClick={() => {
                  if (!conversationPrivacy.toggle) {
                    return;
                  }

                  onPrivateBtnClick?.();
                }}
                data-testid="makeprivate"
                disabled={hidePrivateToggler}
              >
                {
                  // can toggle privacy if conversationPrivacy.toggle is true
                  conversationPrivacy.toggle &&
                    (isPrivate ? <ViewOff16 /> : <View16 />)
                }
                {
                  // if conversationPrivacy.toggle is false, then show private icon if conversationPrivacy.private is true
                  conversationPrivacy.private ? <ViewOff16 /> : null
                }
              </button>
            </span>
          </Tooltip>
        </div>
      </div>
      <div className={styles.actions}>
        <Button
          size="small"
          type="link"
          onClick={() => {
            onClose();
            setFormVisibility(false);
            setContent('');
          }}
          data-testid="cancel"
        >
          <FormattedMessage id="cancel" />
        </Button>
        <Button
          size="small"
          data-testid="submit"
          onClick={onFormSubmit}
          loading={waitingForAPI}
          disabled={!commentContent.length}
          type="primary"
        >
          <FormattedMessage id={submitFormLabel || 'add'} />
        </Button>
      </div>
    </form>
  ) : (
    <ReplyComment
      onClick={() => setFormVisibility(true)}
      avatarSrc={avatarSrc}
      userName={userName}
    />
  );
};
